import { useEffect, useRef, useState } from 'react';
import * as maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { Feature, MultiPolygon, Polygon, Position } from 'geojson';
import {
  PARTIES,
  PARTY_ORDER,
  type CountyAggregate,
  type GlobalAggregate,
  type PartyId,
  type ValkrtsAggregate,
} from '../lib/types';
import { RegionPopup, type ElectionSection } from './RegionPopup';
import {
  countyElectionSections,
  electionSections,
  type ElectionsByYear,
} from '@shared/elections';
import { districtElectionSections, type DistrictElectionData } from '@shared/elections/districts';

interface Props {
  aggregate: GlobalAggregate;
  valkretsar: ValkrtsAggregate[];
  countyAggregates: CountyAggregate[];
  aggregatesLoaded: boolean;
}

const COUNTY_CODES = [
  '01','03','04','05','06','07','08','09','10',
  '12','13','14','17','18','19','20','21','22','23','24','25',
];
const NEUTRAL_COLOR = '#2a3050';
const SWEDEN_CENTER: [number, number] = [16.5, 62.5];

// MapLibre zoom thresholds (tile zoom, not latitudeDelta)
const ZOOM_VALKRETSAR = 5;   // [5, 7.5) shows valkretsar
const ZOOM_DISTRICTS = 7.5;  // [7.5, ∞) shows individual districts

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyExpr = any;

// MapLibre may deliver GeoJSON feature properties as already-parsed objects
// (not strings) depending on version. Guard both cases.
function parseProp<T>(val: unknown): T {
  if (typeof val === 'string') return JSON.parse(val) as T;
  return val as T;
}

function buildMatchExpr(ownership: Record<string, PartyId>): AnyExpr {
  const entries = Object.entries(ownership);
  if (entries.length === 0) return NEUTRAL_COLOR;
  return [
    'match',
    ['get', 'id'],
    ...entries.flatMap(([id, partyId]) => [id, PARTIES[partyId]?.color ?? NEUTRAL_COLOR]),
    NEUTRAL_COLOR,
  ];
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function getAggregateColor(agg: ValkrtsAggregate | CountyAggregate): string {
  let best: PartyId | null = null;
  let bestCount = 0;
  for (const p of PARTY_ORDER) {
    const c = agg.followerCounts[p] ?? 0;
    if (c > bestCount) { bestCount = c; best = p; }
  }
  if (best && bestCount > 0) {
    return hexToRgba(PARTIES[best].color, 0.73);
  }
  return NEUTRAL_COLOR;
}

function featureCentroid(feature: Feature): [number, number] | null {
  const geom = feature.geometry as Polygon | MultiPolygon;
  const ring = geom.type === 'Polygon' ? geom.coordinates[0] : geom.coordinates[0]?.[0];
  if (!ring || ring.length === 0) return null;
  const lonSum = ring.reduce((s, c) => s + c[0], 0);
  const latSum = ring.reduce((s, c) => s + c[1], 0);
  return [lonSum / ring.length, latSum / ring.length];
}

// Below this share, a party's slice would round away to nothing at marker
// width — floor it so early engagement is still visible on the bar.
const MIN_VISIBLE_FRACTION = 0.04;

function buildBarElement(agg: ValkrtsAggregate | CountyAggregate, width: number): HTMLElement | null {
  const total = agg.total_population;
  if (total === 0) return null;
  const slices: { color: string; fraction: number }[] = [];
  for (const p of PARTY_ORDER) {
    const count = agg.followerCounts[p] ?? 0;
    if (count > 0) slices.push({ color: PARTIES[p].color, fraction: Math.max(count / total, MIN_VISIBLE_FRACTION) });
  }
  if (slices.length === 0) return null;
  const partyTotal = slices.reduce((s, x) => s + x.fraction, 0);
  if (partyTotal > 1) {
    const scale = 1 / partyTotal;
    for (const slice of slices) slice.fraction *= scale;
  } else {
    const neutralFraction = 1 - partyTotal;
    if (neutralFraction > 0.005) slices.push({ color: '#999999', fraction: neutralFraction });
  }

  const el = document.createElement('div');
  el.style.cssText = `width:${width}px;height:14px;border-radius:7px;overflow:hidden;display:flex;border:1.5px solid rgba(255,255,255,0.8);pointer-events:none;`;
  for (const slice of slices) {
    const strip = document.createElement('div');
    strip.style.cssText = `width:${slice.fraction * 100}%;height:100%;background-color:${slice.color};flex-shrink:0;`;
    el.appendChild(strip);
  }
  return el;
}

function syncAggMarkers(
  map: maplibregl.Map,
  markersRef: React.MutableRefObject<maplibregl.Marker[]>,
  features: Feature[],
  aggregates: (ValkrtsAggregate | CountyAggregate)[],
  keyField: 'vk_code' | 'county_code',
  barWidth: number,
  visible: boolean,
) {
  markersRef.current.forEach(m => m.remove());
  markersRef.current = [];
  const byKey = new Map<string, ValkrtsAggregate | CountyAggregate>();
  for (const agg of aggregates) {
    const k = keyField === 'vk_code' ? (agg as ValkrtsAggregate).vk_code : (agg as CountyAggregate).county_code;
    byKey.set(k, agg);
  }
  for (const feature of features) {
    const key = ((feature.properties ?? {}) as Record<string, string>)[keyField];
    if (!key) continue;
    const agg = byKey.get(key);
    if (!agg) continue;
    const el = buildBarElement(agg, barWidth);
    if (!el) continue;
    const centroid = featureCentroid(feature);
    if (!centroid) continue;
    el.style.display = visible ? 'flex' : 'none';
    const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
      .setLngLat(centroid)
      .addTo(map);
    markersRef.current.push(marker);
  }
}

function buildAggregateMatchExpr(
  aggregates: ValkrtsAggregate[] | CountyAggregate[],
  keyField: 'vk_code' | 'county_code',
): AnyExpr {
  if (aggregates.length === 0) return NEUTRAL_COLOR;
  const flat: string[] = [];
  for (const agg of aggregates) {
    const key = keyField === 'vk_code'
      ? (agg as ValkrtsAggregate).vk_code
      : (agg as CountyAggregate).county_code;
    flat.push(key, getAggregateColor(agg));
  }
  return ['match', ['get', keyField], ...flat, NEUTRAL_COLOR];
}

function featureBounds(feature: maplibregl.MapGeoJSONFeature): maplibregl.LngLatBoundsLike {
  const geom = feature.geometry as Polygon | MultiPolygon;
  const rings: Position[][] =
    geom.type === 'Polygon'
      ? [geom.coordinates[0]]
      : geom.coordinates.map(p => p[0]);
  const all = rings.flat();
  const lons = all.map(c => c[0]);
  const lats = all.map(c => c[1]);
  return [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]];
}

interface SelectedRegion {
  name: string;
  aggregate: ValkrtsAggregate | CountyAggregate | null;
  electionSections: ElectionSection[];
  bounds: maplibregl.LngLatBoundsLike;
}

export function MapView({ aggregate, valkretsar, countyAggregates, aggregatesLoaded }: Props) {
  const mapInnerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<SelectedRegion | null>(null);

  // Refs so click handlers always see current data without re-registration
  const valkretsarRef = useRef(valkretsar);
  const countyAggregatesRef = useRef(countyAggregates);
  const aggregatesLoadedRef = useRef(aggregatesLoaded);
  const aggregateRef = useRef(aggregate);

  // GeoJSON features for centroid calculation
  const countyFeaturesRef = useRef<Feature[]>([]);
  const valkretsarFeaturesRef = useRef<Feature[]>([]);
  // Party bar markers
  const countyMarkersRef = useRef<maplibregl.Marker[]>([]);
  const valkretsMarkersRef = useRef<maplibregl.Marker[]>([]);
  type VkElection = { elections: ElectionsByYear; rikskrets_name?: string };
  const valkretsElectionRef = useRef<Record<string, VkElection>>({});
  // Per-valdistrikt results, fetched in the background; until they arrive a
  // district click shows its riksdagsvalkrets result instead.
  const districtElectionRef = useRef<DistrictElectionData | null>(null);
  useEffect(() => { valkretsarRef.current = valkretsar; }, [valkretsar]);
  useEffect(() => { countyAggregatesRef.current = countyAggregates; }, [countyAggregates]);
  useEffect(() => { aggregatesLoadedRef.current = aggregatesLoaded; }, [aggregatesLoaded]);
  useEffect(() => { aggregateRef.current = aggregate; }, [aggregate]);

  useEffect(() => {
    if (!mapInnerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: mapInnerRef.current,
      style: {
        version: 8,
        sources: {},
        layers: [{ id: 'background', type: 'background', paint: { 'background-color': '#0d1525' } }],
      },
      center: SWEDEN_CENTER,
      zoom: 3.6,
      attributionControl: { compact: true },
    });

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    map.addControl(
      new maplibregl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: false,
        showAccuracyCircle: true,
      }),
      'top-right',
    );
    mapRef.current = map;

    map.on('load', async () => {
      // ── Districts (zoom >= 7.5) ──────────────────────────────────────────
      fetch('/elections/valdistrikt.json')
        .then(r => r.json())
        .then((d: DistrictElectionData) => { districtElectionRef.current = d; })
        .catch(() => { /* keep the valkrets fallback */ });

      const [valkretsGeoJsonResult, countyOutlinesResult, ...countyResults] = await Promise.allSettled([
        fetch('/geojson/valkretsar.json').then(r => r.json()),
        fetch('/geojson/county-outlines.json').then(r => r.json()),
        ...COUNTY_CODES.map(code => fetch(`/geojson/counties/${code}.json`).then(r => r.json())),
      ]);
      const features: Feature[] = [];
      countyResults.forEach(r => { if (r.status === 'fulfilled') features.push(...r.value.features); });

      // Build vk_code → election index for district popups; store features for centroids
      if (valkretsGeoJsonResult.status === 'fulfilled') {
        valkretsarFeaturesRef.current = valkretsGeoJsonResult.value.features;
        const idx: Record<string, VkElection> = {};
        for (const feat of valkretsGeoJsonResult.value.features) {
          const p = feat.properties as { vk_code: string; rikskrets_name?: string; elections?: string | null };
          if (p.vk_code && p.elections) {
            idx[p.vk_code] = { elections: parseProp(p.elections), rikskrets_name: p.rikskrets_name };
          }
        }
        valkretsElectionRef.current = idx;
      }
      if (countyOutlinesResult.status === 'fulfilled') {
        countyFeaturesRef.current = countyOutlinesResult.value.features;
      }

      map.addSource('districts', { type: 'geojson', data: { type: 'FeatureCollection', features } });
      map.addLayer({
        id: 'districts-fill', type: 'fill', source: 'districts',
        minzoom: ZOOM_DISTRICTS,
        paint: { 'fill-color': NEUTRAL_COLOR, 'fill-opacity': 0.85 },
      });
      map.addLayer({
        id: 'districts-outline', type: 'line', source: 'districts',
        minzoom: ZOOM_DISTRICTS,
        paint: { 'line-color': '#1a2040', 'line-width': 0.3 },
      });

      // ── Valkretsar (zoom 5–7.5) ──────────────────────────────────────────
      const valkretsSourceData = valkretsGeoJsonResult.status === 'fulfilled'
        ? valkretsGeoJsonResult.value
        : '/geojson/valkretsar.json';
      map.addSource('valkretsar-src', { type: 'geojson', data: valkretsSourceData });
      map.addLayer({
        id: 'valkretsar-fill', type: 'fill', source: 'valkretsar-src',
        minzoom: ZOOM_VALKRETSAR, maxzoom: ZOOM_DISTRICTS,
        paint: { 'fill-color': NEUTRAL_COLOR, 'fill-opacity': 0.85 },
      });
      map.addLayer({
        id: 'valkretsar-outline', type: 'line', source: 'valkretsar-src',
        minzoom: ZOOM_VALKRETSAR, maxzoom: ZOOM_DISTRICTS,
        paint: { 'line-color': '#1a2040', 'line-width': 0.5 },
      });

      // ── Counties (zoom < 5) ──────────────────────────────────────────────
      const countyOutlinesData = countyOutlinesResult.status === 'fulfilled'
        ? countyOutlinesResult.value
        : '/geojson/county-outlines.json';
      map.addSource('counties-src', { type: 'geojson', data: countyOutlinesData });
      map.addLayer({
        id: 'counties-fill', type: 'fill', source: 'counties-src',
        maxzoom: ZOOM_VALKRETSAR,
        paint: { 'fill-color': NEUTRAL_COLOR, 'fill-opacity': 0.85 },
      });
      map.addLayer({
        id: 'counties-outline', type: 'line', source: 'counties-src',
        maxzoom: ZOOM_VALKRETSAR,
        paint: { 'line-color': '#1a2040', 'line-width': 0.7 },
      });

      // ── Click: valkrets ──────────────────────────────────────────────────
      map.on('click', 'valkretsar-fill', e => {
        const feat = e.features?.[0];
        if (!feat) return;
        const props = feat.properties as {
          vk_code: string;
          name: string;
          rikskrets_name?: string;
          elections?: string | null;
        };
        const agg = valkretsarRef.current.find(v => v.vk_code === props.vk_code)
          ?? (aggregatesLoadedRef.current ? { followerCounts: {}, total_population: 0, ownedByParty: null } as ValkrtsAggregate : null);
        const sections: ElectionSection[] = props.elections
          ? electionSections(parseProp<ElectionsByYear>(props.elections), props.rikskrets_name)
          : [];
        setSelectedRegion({
          name: props.name ?? props.vk_code,
          aggregate: agg,
          electionSections: sections,
          bounds: featureBounds(feat),
        });
      });

      // ── Click: county ────────────────────────────────────────────────────
      map.on('click', 'counties-fill', e => {
        const feat = e.features?.[0];
        if (!feat) return;
        const props = feat.properties as {
          county_code: string;
          county_name?: string;
          rikskretsar?: string;
        };
        const agg = countyAggregatesRef.current.find(c => c.county_code === props.county_code)
          ?? (aggregatesLoadedRef.current ? { followerCounts: {}, total_population: 0, ownedByParty: null } as CountyAggregate : null);
        const rikskretsar: Array<{ key: string; name: string; elections: ElectionsByYear | null }> =
          props.rikskretsar ? parseProp(props.rikskretsar) : [];
        setSelectedRegion({
          name: props.county_name ?? props.county_code,
          aggregate: agg,
          electionSections: countyElectionSections(rikskretsar),
          bounds: featureBounds(feat),
        });
      });

      // ── Click: district ─────────────────────────────────────────────────
      map.on('click', 'districts-fill', e => {
        const feat = e.features?.[0];
        if (!feat) return;
        const props = feat.properties as {
          id: string;
          name: string;
          population: number;
          vk_code: string;
        };
        const ownership = aggregateRef.current.districtMajority ?? aggregateRef.current.districtOwnership ?? {};
        const ownedByParty = (ownership[props.id] ?? null) as PartyId | null;
        const vkElection = valkretsElectionRef.current[props.vk_code];
        // The district's own result; its riksdagsvalkrets' until that file has loaded.
        const districtData = districtElectionRef.current;
        const sections: ElectionSection[] = districtData
          ? districtElectionSections(districtData, props.id)
          : vkElection
          ? electionSections(vkElection.elections, vkElection.rikskrets_name)
          : [];
        setSelectedRegion({
          name: props.name,
          aggregate: { followerCounts: {}, total_population: props.population, ownedByParty } as ValkrtsAggregate,
          electionSections: sections,
          bounds: featureBounds(feat),
        });
      });

      // ── Pointer cursor on hover ──────────────────────────────────────────
      for (const layer of ['districts-fill', 'valkretsar-fill', 'counties-fill']) {
        map.on('mouseenter', layer, () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', layer, () => { map.getCanvas().style.cursor = ''; });
      }

      // ── Show/hide bar markers on zoom ────────────────────────────────────
      map.on('zoom', () => {
        const z = map.getZoom();
        countyMarkersRef.current.forEach(m => {
          m.getElement().style.display = z < ZOOM_VALKRETSAR ? 'flex' : 'none';
        });
        valkretsMarkersRef.current.forEach(m => {
          m.getElement().style.display = (z >= ZOOM_VALKRETSAR && z < ZOOM_DISTRICTS) ? 'flex' : 'none';
        });
      });

      setMapLoaded(true);
    });

    return () => {
      countyMarkersRef.current.forEach(m => m.remove());
      countyMarkersRef.current = [];
      valkretsMarkersRef.current.forEach(m => m.remove());
      valkretsMarkersRef.current = [];
      map.remove();
      mapRef.current = null;
      setMapLoaded(false);
    };
  }, []);

  // Recolor districts
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const coloring = aggregate.districtMajority ?? aggregate.districtOwnership ?? {};
    map.setPaintProperty('districts-fill', 'fill-color', buildMatchExpr(coloring));
  }, [mapLoaded, aggregate.districtMajority, aggregate.districtOwnership]);

  // Recolor valkretsar
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    map.setPaintProperty('valkretsar-fill', 'fill-color', buildAggregateMatchExpr(valkretsar, 'vk_code'));
  }, [mapLoaded, valkretsar]);

  // Recolor counties
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    map.setPaintProperty('counties-fill', 'fill-color', buildAggregateMatchExpr(countyAggregates, 'county_code'));
  }, [mapLoaded, countyAggregates]);

  // Sync county bar markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const visible = map.getZoom() < ZOOM_VALKRETSAR;
    syncAggMarkers(map, countyMarkersRef, countyFeaturesRef.current, countyAggregates, 'county_code', 68, visible);
  }, [mapLoaded, countyAggregates]);

  // Sync valkrets bar markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const z = map.getZoom();
    const visible = z >= ZOOM_VALKRETSAR && z < ZOOM_DISTRICTS;
    syncAggMarkers(map, valkretsMarkersRef, valkretsarFeaturesRef.current, valkretsar, 'vk_code', 52, visible);
  }, [mapLoaded, valkretsar]);

  const handleZoomIn = () => {
    if (!selectedRegion) return;
    mapRef.current?.fitBounds(selectedRegion.bounds as maplibregl.LngLatBoundsLike, { padding: 40, duration: 600 });
    setSelectedRegion(null);
  };

  return (
    <div className="map-container" style={{ position: 'relative' }}>
      <div ref={mapInnerRef} style={{ width: '100%', height: '100%' }} />
      {selectedRegion && (
        <RegionPopup
          name={selectedRegion.name}
          aggregate={selectedRegion.aggregate}
          electionSections={selectedRegion.electionSections}
          onClose={() => setSelectedRegion(null)}
          onZoomIn={handleZoomIn}
        />
      )}
    </div>
  );
}
